/**
 * Asking about one thing on the graph, without changing the graph.
 *
 * This is the one channel in the app that is deliberately outside the op system,
 * and every line of that is intentional:
 *
 *  - **No `beginOp`, no `busy`, no undo mark, no node writes.** A conversation is
 *    not a move in the argument, so it takes no step of history and leaves no
 *    trace on the canvas. It also means asking a question does not interrupt a
 *    run: the loop's lock and this channel never contend, because this one never
 *    takes the lock.
 *  - **Its own `AbortController`.** `inFlight` in `reason.ts` belongs to the pass
 *    the run is inside; sharing it would make pressing Stop on a run cancel a
 *    question, and closing a question cancel the run.
 *  - **It cannot search.** `streamText` declares no tools, so an answer here is
 *    the model reading what is already on the graph. An answer with model-written
 *    citations would walk past the citation gate every source check in this app
 *    rests on, and the honest way to want a source is to run the check.
 *  - **It is billed.** The spend goes on the same per-model ledger as everything
 *    else, because it costs the same money.
 *
 * What it produces is never written back. A conversation can speculate, and
 * speculation that became a node would be indistinguishable from reasoning the
 * graph paid for.
 */

import type { Bridge, Op, ReasonNode, Session, Spend } from '../core/types'
import {
  READING_LABEL,
  endLabel,
  reasoningOf,
  spendGoesTo,
  verificationLive,
  kindLabelOf,
} from '../core/types'
import { closedMark } from '../core/endings'
import { lineage, providerFor, resolveFor, useStore, type ChatTarget } from '../core/store'
import { onRenamed } from '../core/rename'
import { decodeEntities } from '../core/entities'
import { stanceLabel } from '../core/vocabulary'
import { calendarDay } from '../core/dates'
import { selfRatingsOf } from '../core/rewrite'
import { DISPUTED_RULE, readingMark, recordDigest, restsOnDisputed } from '../core/recordDigest'
import { retainNarration } from '../core/narrationReceipt'
import { recordNarrationOn, recordSpendOn } from '../core/autosave'
import { declinedMessage, watchQuiet } from '../core/failure'
import { ModelError, streamText, type RetryWait, type TextTurn } from './transport'
import { resolvePassEffort } from './effort'
import { BRIEF_FOR, FRAME_BRIEF, buildBriefBlock, dateLine } from './prompts'
import { actsProtocol, actsStreamFilter, actsTable, parseActs, type ProposedAct } from './acts'
import { refsProtocol } from './refs'

/** One question, its answer, and whether the answer is still arriving. */
export interface ChatTurn {
  id: string
  question: string
  answer: string
  /** True while the answer is streaming. */
  live: boolean
  /** Set when the turn failed or was declined, in which case `answer` is empty. */
  error?: string
}

let controller: AbortController | null = null

/**
 * WHICH DOCUMENT owes for an answer still in flight — not which name.
 *
 * The id alone was not enough, because `s_demo` is reused: see `spendGoesTo`,
 * which owns the comparison. `born` is the moment that document began, so it
 * survives the rename below (same document, new name) and moves only when the
 * document itself is replaced.
 */
export interface Payer {
  id: string
  born: number
}

/**
 * Which exploration each answer still in flight is being charged to.
 *
 * A holder rather than a captured string, because the demo's paid remint renames
 * the document mid-answer and this has to follow it. Left naming `s_demo`, the
 * receipt would be written to a document that has never existed — the worked
 * example is built in memory and saved under its new name — and a real charge
 * would be dropped as unattributable. `core/rename.ts` is how the store says the
 * rename happened; guessing it from the id is what this codebase already tried
 * and reverted.
 *
 * A SET, not one holder, and the difference is a receipt landing on the wrong
 * graph. `ask` aborts whatever was running before it starts, so at most one
 * answer is arriving — but an aborted request has not SETTLED, and its promise
 * still returns whatever was spent before the abort. Put a conversation down and
 * ask again, and for a moment two requests are unsettled while a single holder
 * names only the newer. A remint in that moment updated one and left the other
 * charging a name the app had stopped using.
 *
 * Each entry leaves when its own promise finishes, which is the only moment its
 * receipt can no longer be written.
 */
const paying = new Set<Payer>()

onRenamed(({ from, to }) => {
  for (const p of paying) if (p.id === from) p.id = to
})

/** Stop the answer in flight. Leaves what has arrived, like every other stop in
 *  this app: a partial answer is still an answer, and it was billed. */
export function stopAsking(): void {
  controller?.abort()
  controller = null
}

/**
 * Take out a payer for one request on this channel, and give it back when the
 * request settles.
 *
 * Exported for the narrator (`ui/narrator.ts`), which speaks on this same
 * unchecked channel and owes its receipts under the same rules — the paying
 * set and the rename hook above cover every payer minted here, so a second
 * copy of that machinery in the narrator would be the drift this file's own
 * `Payer` comment warns about.
 */
export function openPayer(): Payer {
  const s = useStore.getState()
  const p: Payer = { id: s.session.id, born: s.session.createdAt }
  paying.add(p)
  return p
}

export function closePayer(p: Payer): void {
  paying.delete(p)
}

/**
 * Put the receipt where it was earned.
 *
 * The open exploration's ledger is the store's and is held in memory; a closed
 * one's lives in its document, and writing a document can evict, so it goes
 * through the writer that knows about the archive. The fork is here rather than
 * inside `addSpend` because `core/store.ts` deliberately does not know the
 * archive exists — see D38.
 */
export function bankNarration(operation: Op, paidBy: Payer): boolean {
  switch (spendGoesTo(useStore.getState().session, paidBy)) {
    case 'open': {
      const session = retainNarration(useStore.getState().session, operation)
      if (!session) return false
      useStore.setState({ session })
      return true
    }
    case 'stored':
      return recordNarrationOn(paidBy.id, operation, paidBy.born)
    case 'nowhere':
      return false
  }
}

export function bankSpend(row: Spend, paidBy: Payer): void {
  // Exhaustive, with no default: a destination added to `SpendDestination` stops
  // the build until somebody decides where that receipt goes, rather than
  // falling through to the open exploration — which is the defect D38 removed.
  switch (spendGoesTo(useStore.getState().session, paidBy)) {
    case 'open':
      useStore.getState().addSpend(row)
      return
    case 'stored':
      // The payer's own identity goes with it: this branch was reached because
      // the NAMES differ, which says nothing about the document now stored under
      // the payer's name. `recordSpendOn` refuses one that began at another time.
      recordSpendOn(paidBy.id, row, paidBy.born)
      return
    case 'nowhere':
      // The document that owed this is gone, and its name has been given to
      // another. See `spendGoesTo` for why that is a drop rather than a fallback.
      return
  }
}

/**
 * The system brief for a conversation.
 *
 * It says three things the model must not get wrong: what it is looking at, that
 * it is not editing anything, and that its own recollection is not a source. The
 * last is the one that matters — without it a model asked "is this true?" about a
 * claim on the canvas will answer with confident half-remembered citations, and
 * this channel has no gate that could catch them.
 *
 * The shape rules are a word limit rather than a paragraph count, because the
 * count was broken on every answer a usability study collected: 313 to 453
 * words against "two or three short paragraphs", each one under bold headings
 * the thread printed as raw asterisks. A word limit is a number the model can
 * hold itself to. The contract itself carries no bold for the same reason — a
 * prompt written in `**` gets answers written in `**`. And the plumbing rule is
 * there because a reader was once told "Running V (verify) on n_muesrfwp005
 * would confirm those details": a key, a verb's internal name and a node id, in
 * one sentence addressed to someone who knows none of them.
 */
const CHAT_SYSTEM = (
  about: string,
  purpose: string,
) => `You are answering questions about ${about} in Pathways — a tool that takes an idea apart into a graph of arguable claims.

You are not editing the graph. Nothing you write here becomes a node, and you must not propose one: if the answer is "this needs a whole branch", say what the branch would examine and leave it to the person to run.

What you are for: ${purpose} Explain the mechanism, name what it rests on, say plainly where it is weak, and answer the question actually asked.

How you write:
- At most 200 words, and fewer when fewer will do.
- Plain paragraphs. No headings and no bold.
- Name another claim by its label in quotes, the way the graph shows it — never by an id or a number. "The parent" or "the seed" is fine.
- Call a move by the name the person sees: a source check, Challenge, Deepen. Never by a key or an internal name. Write: A source check on "<the claim's label>" would settle that.
- Define a technical term in a few words the first time you use it.
- Do not say whose side a finding is on. Say which claim it supports or weakens.

Three rules you do not break:

1. You have no search on this turn. You cannot open a page, and you have not read one. If the honest answer needs a source, say which source would settle it, and that a source check on the claim is what would fetch it. Never write a URL, a DOI, or a citation as though you had verified it — anything you name is a recollection, and you say so in those words.
2. Separate what the graph says from what you think. The claim, its reasoning and any recorded result are on the canvas and are quoted to you below. Where your reading differs from them, say which is which. A source check reports a reading of what its sources said, not a verdict on the claim, and a confidence figure is the model's own rating, not a probability.
3. A recorded result outranks you. If the node carries an observation someone entered, that is the one thing here that came from the world. Do not argue it away; explain what it implies.`

/**
 * What Ask is told a conjecture is, in the reader's word (D333).
 *
 * `CHAT_SYSTEM` tells the model to call a move by the name the person sees, and
 * the node line now prints "Insight" — a word that in plain English leans toward
 * a settled finding, the one reading a proposal nothing outside the model has
 * tested must not get (invariant 12). So the gloss rides on the brief of a
 * conjecture node, and on the whole-graph brief when it lists one, and never in
 * `CHAT_SYSTEM`: those bytes are the cached head every conversation shares, and
 * every other brief stays exactly as it was.
 * Each "never" opens its own clause, as the narrator's do, so the invariant-12
 * scan reads every banned word under a denial.
 */
const INSIGHT_GLOSS =
  'WHAT THE APP CALLS AN INSIGHT — a proposal with a prediction that nothing outside the model has tested. Never call it new or novel or original, never a discovery or a breakthrough.'

/** The node, its branch, and everything already known about it. The same shape the
 *  reasoning prompts use, so a conversation and a pass are looking at one graph. */
function nodeBrief(s: Session, n: ReasonNode): string {
  const path = lineage(s, n.id)
  const above = path
    .slice(0, -1)
    .map((p, i) => `${'  '.repeat(i)}${p.label} — ${p.claim}`)
    .join('\n')
  const v = n.verification
  const kids = s.order.filter((id) => s.nodes[id]?.parentId === n.id).length
  const reasoning = reasoningOf(n)

  return `THE SEED THIS SITS UNDER
${s.seed}

${above ? `THE ARGUMENT ABOVE IT\n${above}\n` : ''}${'─'.repeat(60)}

THE NODE
${n.label} — ${kindLabelOf(n)}${n.terminal ? `, ${closedMark(n)}` : ''}
${n.claim}

${n.kind === 'conjecture' && n.conjecture ? `${INSIGHT_GLOSS}\n` : ''}${reasoning ? `ITS STATED REASONING\n${reasoning}\n` : ''}${
    n.evidence.length
      ? `POINTERS IT WROTE FROM MEMORY — unverified, and treat them as such\n${n.evidence.map((e) => `- ${e}`).join('\n')}\n`
      : ''
  }${n.terminalReason ? `WHY THE MODEL CLOSED IT\n${n.terminalReason}\n` : ''}${
    n.outcome
      ? `A RESULT SOMEONE RECORDED HERE — this came from the world and outranks everything above\n${n.outcome.observed}\n(It went ${n.outcome.bearing === 'supported' ? 'the way the claim needed' : n.outcome.bearing === 'refuted' ? 'against the claim' : 'neither way'}.)\n`
      : ''
  }${
    v
      ? `WHAT A SOURCE CHECK FOUND${verificationLive(n) ? '' : ' ON AN EARLIER WORDING — the claim has been rewritten since, so this read different words'} — ${READING_LABEL[v.reading]}\n${v.summary ?? 'Nothing carried over from that pass.'}\n${v.sources.map((x) => `- ${decodeEntities(x.title)} (${stanceLabel(x)})`).join('\n')}\n`
      : ''
  }${n.panel ? `THREE LENSES READ IT SEPARATELY\n${n.panel.votes.map((x) => `- ${x.lens}: ${x.verdict} — ${x.because}`).join('\n')}\n` : ''}
${ratingsLine(n)} ${kids} branch${kids === 1 ? '' : 'es'} derive from here.`
}

/**
 * The model's self-ratings, where they still rate the words on the node.
 *
 * A claim the reader rewrote keeps the numbers the model gave the wording it
 * replaced, and the card, the exports and the canvas stopped showing them for it
 * (`selfRatingsOf`, D189). Ask was still handed them, so the answer could weigh a
 * rewrite by a confidence nobody gave its words.
 */
function ratingsLine(n: ReasonNode): string {
  const own = selfRatingsOf(n)
  return own
    ? `The model's own self-ratings for this node: confidence ${own.confidence.toFixed(2)}, novelty ${own.novelty.toFixed(2)}, tension ${own.tension.toFixed(2)}. They are its own numbers on its own scale — a sort order, not probabilities.`
    : n.carriedFrom !== undefined
      ? `This is the expectation an insight departs from, set down with that insight, so it has no self-ratings: no model rated it.`
      : `The reader rewrote this claim, so it has no self-ratings: the model's numbers rated the wording it replaced, not these words.`
}

/** …and the same in the graph's outline, one figure per line. */
const confMark = (n: ReasonNode): string => {
  const own = selfRatingsOf(n)
  if (own) return `conf ${own.confidence.toFixed(2)}`
  // Two reasons a node has no rating of its own, and Ask is told which: its
  // numbers rated words since replaced, or nobody gave it any (D284).
  return n.carriedFrom !== undefined
    ? 'set down with an insight, unrated'
    : 'rewritten by the reader, unrated'
}

/** A candidate synthesis, which is a pair rather than a node — so the brief names
 *  both ends and says the score is the model's own hint. */
function bridgeBrief(s: Session, b: Bridge): string {
  const a = endLabel(b.a, s.nodes) ?? 'a branch since pruned'
  const z = endLabel(b.b, s.nodes) ?? 'a branch since pruned'
  const claimOf = (e: typeof b.a) =>
    e.where === 'foreign' ? e.ref.claim : (s.nodes[e.nodeId]?.claim ?? '')
  // The card this conversation sits under says so, and an answer that did not
  // know would argue for a fusion through a claim the person's own check read
  // against — the failure the mark was built for, one surface over.
  const disputed = restsOnDisputed(s.nodes, b)
  return `THE SEED THIS SITS UNDER
${s.seed}

${'─'.repeat(60)}

A CANDIDATE SYNTHESIS — a pair a scan proposed, which nobody has realised
${b.title}
${b.why}

ONE END: ${a}
${claimOf(b.a)}

THE OTHER END: ${z}
${claimOf(b.b)}
${
  disputed.length
    ? `\nTHIS PAIR RESTS ON A CLAIM A CHECK DISPUTED — marked because ${DISPUTED_RULE}\n${disputed.map((n) => `- "${n.label}" — ${readingMark(n)}`).join('\n')}\n`
    : ''
}
The scan's own score for this pair is ${b.score.toFixed(2)} — its own hint on its own scale, and explicitly not a verdict on whether the two fuse. Whether they actually do is the judgement this tool leaves to a person, and it is what the question below is probably about.`
}

/**
 * The whole graph at once, indented under what each claim derives from.
 *
 * Compact by construction: one line per node, claims clipped, and the deepest
 * lines dropped past a cap — a conversation about the shape of an argument does
 * not need every word of it, and an unbounded brief would spend real money per
 * turn on a graph the model mostly does not quote.
 */
function graphBrief(s: Session): string {
  const kids = new Map<string, string[]>()
  let root: string | null = null
  for (const id of s.order) {
    const n = s.nodes[id]
    if (!n) continue
    if (n.parentId === null) root = id
    else {
      const list = kids.get(n.parentId)
      if (list) list.push(id)
      else kids.set(n.parentId, [id])
    }
  }
  const clip = (t: string, m: number) => (t.length > m ? `${t.slice(0, m - 1)}…` : t)
  const CAP = 60
  const lines: string[] = []
  let dropped = 0
  // Whether a listed line prints a conjecture, as "[Insight, …]" since D333.
  let insightListed = false
  const walk = (id: string, depth: number) => {
    const n = s.nodes[id]
    if (!n) return
    if (n.parentId !== null) {
      if (lines.length >= CAP) dropped++
      else {
        const marks = [
          closedMark(n) ?? '',
          n.outcome ? `A RESULT WAS RECORDED HERE (went ${n.outcome.bearing})` : '',
          checkMark(n),
          n.panel ? 'a panel read it three ways' : '',
        ]
          .filter(Boolean)
          .join('; ')
        lines.push(
          `${'  '.repeat(depth - 1)}- ${n.label} [${kindLabelOf(n)}, ${confMark(n)}]${marks ? ` (${marks})` : ''} — ${clip(n.claim, 150)}`,
        )
        if (n.kind === 'conjecture' && n.conjecture) insightListed = true
      }
    }
    for (const k of kids.get(id) ?? []) walk(k, depth + 1)
  }
  if (root) walk(root, 0)
  // The tree above is the argument's SHAPE, and it is capped by depth; the
  // record below is what was found, gathered by kind, so a dead end or a check
  // sixty lines down is still in front of the answer. The same digest the voice
  // and the scan read (`core/recordDigest.ts`).
  const record = recordDigest(s)
  // The whole-graph brief prints a conjecture as "[Insight, …]" too, and it is
  // the brief the seed's Ask reads — the most natural question anyone asks. So
  // the gloss rides here once, under the tree, whenever a listed line is one: the
  // node brief's reason, on the brief and never in `CHAT_SYSTEM`, so the cached
  // head is the same bytes and a graph without an insight reads as it did (D333).

  return `THE SEED — the claim this whole graph takes apart
${s.seed}

${'─'.repeat(60)}

EVERY BRANCH, indented under what it derives from${dropped ? ` (the ${dropped} deepest lines are omitted for length)` : ''}
${lines.join('\n') || '(nothing has been derived yet)'}
${insightListed ? `\n${INSIGHT_GLOSS}\n` : ''}${record ? `\nWHAT THE RECORD HOLDS — dead ends, open questions, checks and splits, in one place\n${record}\n` : ''}
Confidence figures are the model's own self-ratings on its own scale — a sort order, not probabilities. A line that says a result was recorded is the one kind that came from the world, and it outranks the reasoning around it.`
}

/**
 * A node's source-check mark in the graph brief, dated, and only where the check
 * still describes the words on the node. A check of since-rewritten wording is
 * said to be one, rather than reported as the claim's current reading.
 */
function checkMark(n: ReasonNode): string {
  const v = n.verification
  if (!v) return ''
  // The reader's calendar day, the one the card prints beside this check (D210).
  const on = calendarDay(v.checkedAt)
  return verificationLive(n)
    ? `source check on ${on} read ${READING_LABEL[v.reading]}`
    : `a source check on ${on} read an earlier wording; the claim has been rewritten since`
}

/** What the conversation is looking at, as the model sees it. Returns null when
 *  the target has been pruned or the candidate withdrawn since it was pinned. */
export function briefFor(s: Session, t: ChatTarget): { title: string; brief: string } | null {
  if (t.kind === 'graph') {
    return { title: s.seed || 'This exploration', brief: graphBrief(s) }
  }
  if (t.kind === 'node') {
    const n = s.nodes[t.id]
    if (!n) return null
    // The seed's own brief is its text and a child count, and the seed is what is
    // selected right after seeding — so the most natural question anyone asks,
    // "what do we know and what has been checked?", was answered from a list of
    // labels. Three people were told confidently that nothing had been checked on
    // graphs holding one, four and five checks. On the root the whole graph IS
    // the subject, so it gets the whole-graph brief, which carries every reading
    // and every recorded result. The target stays the seed node, so its presses
    // and its pinned conversation are unchanged.
    return { title: n.label, brief: n.parentId === null ? graphBrief(s) : nodeBrief(s, n) }
  }
  const b = s.bridges.find((x) => x.id === t.id)
  return b ? { title: b.title, brief: bridgeBrief(s, b) } : null
}

/**
 * The three tiers of a conversation's system prompt, split where the bytes stop
 * being stable.
 *
 * The head is the contract, the frame and the standing brief: the same bytes
 * for the whole life of a conversation, and the block the adapter marks first.
 * The dynamic block is the thing under discussion and — when proposals are
 * wanted — the rules for offering them: the same bytes from one turn to the
 * next until a pass lands, and marked on its own so that turn re-writes it and
 * still reads the head. The tail is the id table those rules quote from: every
 * node on the graph, so it changes whenever a pass lands — and the D51 flow is
 * exactly a pass landing between two turns. Inside the marked block it turned
 * every such turn into a full write of the whole prompt; as `systemTail` it is
 * plain input after a prefix that still reads.
 *
 * The brief was in the head at first, and a review caught what the test had
 * not: the head was pinned against a graph that grew under the SAME brief, while
 * `ask` recomputes the brief from the graph on every turn. Pure, and exported
 * for its test: the property that matters is that the head is a function of
 * the target and nothing on the graph, and that a changed brief moves the
 * dynamic block and nothing else.
 */
export function askSystem(
  s: Pick<Session, 'frame' | 'brief' | 'nodes' | 'order'>,
  brief: string,
  about: string,
  purpose: string,
  proposeActs: boolean,
): { system: string; systemDynamic: string; systemTail?: string } {
  // Frame and standing brief between the contract and the node: a
  // conversation about a route must not discuss it as a truth-claim, and an
  // answer that ignored the user's own constraints would advise them into
  // ground they closed. Chat is the one surface that sees all four kinds —
  // it produces no graph record, so nothing here can launder a stipulation
  // into a finding.
  //
  // The head ends at the rule. Everything above it is the same bytes for the
  // whole life of a conversation whatever happens to the graph, which is what
  // the first marker needs of it.
  const b = buildBriefBlock(s.brief, BRIEF_FOR.chat)
  const system = `${CHAT_SYSTEM(about, purpose)}${
    (s.frame ?? 'claim') === 'claim' ? '' : `\n\n${FRAME_BRIEF[s.frame!]}`
  }${b ? `\n\n${b}` : ''}\n\n${'═'.repeat(60)}`
  // The thing under discussion, and — when proposals are wanted — the rules
  // for offering them, as the second marked block. `briefFor` recomputes the
  // brief from the live graph on every turn, so a pass landing between two
  // turns changes it — a child count, a verification, an outcome — and inside
  // the head that turn re-wrote the whole prompt. Its own block re-writes
  // itself and reads the head. The protocol rides BELOW the brief, and the id
  // table below the protocol: the table is graph content, and the rule sits
  // beside the ids it governs.
  //
  // The marks rule (`engine/refs.ts`, D295) rides with it and for the same
  // reason: it quotes the same table, so it goes wherever the table goes and
  // nowhere else. The head stays the contract alone — a pinned candidate thread
  // sends no table, and a rule there naming "the table below" would name
  // nothing.
  const systemDynamic = `${brief}${
    proposeActs ? `\n\n${'═'.repeat(60)}\n\n${actsProtocol()}\n\n${refsProtocol()}` : ''
  }`
  return proposeActs
    ? { system, systemDynamic, systemTail: actsTable(s) }
    : { system, systemDynamic }
}

/** The most one answer may write. Exported so the marks harness bounds a live
 *  run by the cap the call actually carries. */
export const ASK_MAX_TOKENS = 1600

/**
 * What the system contract calls the thing under discussion — a node, a
 * candidate pair, or the whole argument at once — and what the answer is for.
 * A question on the seed is about the whole graph (D113). Exported so the
 * marks harness (`evals/marks`) builds the exact request `ask` sends rather
 * than a copy of it.
 */
export function askFrame(
  target: ChatTarget,
  nodes: Session['nodes'],
): readonly [about: string, purpose: string] {
  const wholeGraph =
    target.kind === 'graph' || (target.kind === 'node' && nodes[target.id]?.parentId === null)
  return wholeGraph
    ? ['a WHOLE reasoning graph', 'making the shape of the argument legible.']
    : target.kind === 'bridge'
      ? ['ONE candidate synthesis on a reasoning graph', 'making one pairing legible.']
      : ['ONE node of a reasoning graph', 'making one claim legible.']
}

/**
 * Ask one question. Resolves when the answer has finished arriving.
 *
 * `onDelta` is called per fragment so the panel can render a sentence at a time;
 * the return value is the whole answer, for the transcript.
 */
export async function ask(
  target: ChatTarget,
  question: string,
  history: TextTurn[],
  onDelta: (text: string) => void,
  opts: {
    /**
     * Let the reply end with the action trailer (`engine/acts.ts`): the model
     * may OFFER passes as data, which the feed renders as priced chips only a
     * person can press. The trailer is stripped from the stream before a
     * character of it shows, and everything it names is validated against the
     * app's own vocabulary and the graph's own nodes — a misread is fewer
     * chips, never a wrong spend. Off for the pinned candidate threads, whose
     * subject is a pair no single-node pass can act on.
     */
    proposeActs?: boolean
    /** Hears the transport's waits to ask again, for the turn to say so. */
    onWait?: (w: RetryWait) => void
    /** Hears how long the answer has gone quiet, past the quiet watch's
     *  threshold, and null once anything arrives (`watchQuiet`). */
    onQuiet?: (ms: number | null) => void
  } = {},
): Promise<{
  text: string
  error?: string
  errorKind?: ModelError['kind']
  /** No key for the house Asking resolves to: nothing was sent. */
  keyless?: true
  acts?: ProposedAct[]
}> {
  const s = useStore.getState()
  const m = resolveFor(s, 'chat')
  if (!m) {
    // The house Asking resolves to — the routed one where routed, never the
    // selection's in its place (invariant 4). Its card opens on the key, and
    // `keyless` rides out so the surface that sent the question hands it back,
    // as it does for a key the provider rejected.
    s.askForMissingKey('chat')
    return {
      text: '',
      error: `Asking needs your ${providerFor(s, 'chat').name} key.`,
      keyless: true,
    }
  }

  const found = briefFor(s.session, target)
  if (!found) {
    return {
      text: '',
      error:
        target.kind === 'node'
          ? 'That node is no longer on the graph, so there is nothing left to ask about.'
          : 'That candidate is no longer in the list.',
    }
  }

  const [about, purpose] = askFrame(target, s.session.nodes)

  // WHICH exploration is paying, taken before the await and offered back after
  // it. This channel takes no lock — that is its whole justification — so the
  // reader can open something else while the answer is arriving, and `addSpend`
  // reads the store as it is when the tokens land. Without this the receipt went
  // to whatever was open by then: the graph that asked showed less than it
  // spent, and a graph that never asked showed spend. Every other caller omits
  // it, correctly, because a run holds `busy` and cannot be left mid-pass.
  const paidBy = openPayer()

  // Its own controller. `inFlight` belongs to whatever pass the run is inside, and
  // sharing it would make one Stop cancel two unrelated things.
  stopAsking()
  const c = new AbortController()
  controller = c

  // The display filter, in propose mode only: prose streams through it and the
  // trailer never reaches a delta. Without it, deltas pass untouched — the
  // ordinary ask is byte-identical to what it always was.
  const filter = opts.proposeActs ? actsStreamFilter() : null

  let text = ''
  // The quiet watch a lone pass has, on the same threshold: an answer stalled
  // after three words read as a model still writing, caret and all, for as long
  // as the socket was held. Started here, as the request goes out, and counted
  // on the raw deltas — a trailer the filter holds back is still something heard.
  const quiet = opts.onQuiet ? watchQuiet(opts.onQuiet) : null
  try {
    const res = await streamText({
      model: m,
      // The stable head under the cache marker, the id table after it — see
      // `askSystem` for why the split is where it is.
      ...askSystem(s.session, found.brief, about, purpose, opts.proposeActs === true),
      // Today's date on the newest turn only; `history` carries the questions as
      // they were asked. See `dateLine`.
      user: `${dateLine(Date.now())}\n\n${question}`,
      history,
      // Low, and deliberately. This is a question about text already on screen, and
      // a conversation that takes forty seconds a turn is one nobody has. The
      // reasoning verbs are where the effort belongs — see the `chat` row in
      // `effort.ts`.
      effort: resolvePassEffort({ pass: 'chat', levels: s.effortLevels }),
      maxTokens: ASK_MAX_TOKENS,
      signal: c.signal,
      ...(opts.onWait || quiet
        ? {
            onWait: (w: RetryWait) => {
              quiet?.waiting(w.ms > 0)
              opts.onWait?.(w)
            },
          }
        : {}),
      // The whole system block is invariant across a conversation's turns, and it
      // is long — the node, its branch, its check, its panel. This is the second
      // call site in the app that earns a cache key.
      // A graph target has no id of its own; the session's stands in, so a new
      // exploration is a new cache entry rather than a stale one.
      cacheKey: `ask:${target.kind}:${'id' in target ? target.id : s.session.id}`,
      onText: (d) => {
        quiet?.heard()
        const out = filter ? filter.push(d) : d
        if (out) {
          text += out
          onDelta(out)
        }
      },
    })
    if (filter) {
      // A held suffix that never became the trailer belongs to the reader.
      const rest = filter.flush()
      if (rest) {
        text += rest
        onDelta(rest)
      }
    }
    // The same ledger as every other call. A question costs money, so it appears
    // where the money appears.
    bankSpend(res.spend, paidBy)
    // The trailer, validated into data — or into nothing, the safe direction.
    // `shown` is the filter's own final word on the visible text (trailing
    // whitespace before the trailer trimmed), so the stored answer and the
    // streamed one cannot disagree by more than that trim.
    const split = filter?.result()
    const acts = split?.trailer ? parseActs(split.trailer, s.session) : []
    const finalText = split ? split.shown : text
    if (res.refused) {
      return {
        text: finalText,
        error: declinedMessage('The model declined to answer that.', res, res.spend.model, 'chat'),
      }
    }
    return { text: finalText, ...(acts.length ? { acts } : {}) }
  } catch (err) {
    const partial = filter ? filter.result().shown : text
    if (err instanceof ModelError) {
      if (err.spent) bankSpend(err.spent, paidBy)
      if (err.kind === 'aborted') return { text: partial }
      if (err.kind === 'auth') useStore.getState().askForKey(m.entry.provider)
      // The kind rides out beside the sentence, so the surface that sent the
      // question can hand it back to its box when the key was what failed.
      return { text: partial, error: err.message, errorKind: err.kind }
    }
    return { text: partial, error: 'Something went wrong talking to the model.' }
  } finally {
    quiet?.stop()
    if (controller === c) controller = null
    closePayer(paidBy)
  }
}
